/**
 * 附件页布局工具（T09）。
 *
 * 约定：页面里每个可见部件的逻辑布局都用「390 x 844 画布坐标」声明 —— 这样登记进
 * `src/ui/registry.tsx` 的 anchor 可以直接用于安卓移植清单；而 DOM 实际挂在内容区
 * （`app.content`，y 从 24 开始）内，所以渲染时统一把坐标换算成相对样式。
 */

import type { CSSProperties } from 'react';

import type { BoxLayout } from '../../ui';

/** 内容区在画布中的 y 偏移，与 App.tsx 的 app.content 保持一致。*/
export const CONTENT_TOP = 24;

const CONTENT_ORIGIN: BoxLayout = { x: 0, y: CONTENT_TOP, width: 0, height: 0 };

/** 画布坐标 → 内容区内的绝对定位样式（挂在内容区里的一级部件用）。*/
export function contentBoxStyle(layout: BoxLayout): CSSProperties {
  return offsetBoxStyle(layout, CONTENT_ORIGIN);
}

/** 画布坐标 → 相对某个已定位父级的绝对定位样式（父级必须同为画布坐标）。*/
export function offsetBoxStyle(layout: BoxLayout, parent: BoxLayout): CSSProperties {
  return {
    position: 'absolute',
    left: layout.x - parent.x,
    top: layout.y - parent.y,
    width: layout.width,
    height: layout.height,
  };
}

/** 预览条单个条目尺寸 / 间距 / 移除触摸目标（≥44dp）。*/
export const STRIP_ITEM_WIDTH = 96;
export const STRIP_ITEM_HEIGHT = 100;
export const STRIP_ITEM_GAP = 8;
export const STRIP_REMOVE_SIZE = 44;

/** 第 index 个预览条条目在画布坐标里的位置。*/
export function stripItemLayout(parent: BoxLayout, index: number): BoxLayout {
  return {
    x: parent.x + STRIP_ITEM_GAP + index * (STRIP_ITEM_WIDTH + STRIP_ITEM_GAP),
    y: parent.y + STRIP_ITEM_GAP,
    width: STRIP_ITEM_WIDTH,
    height: STRIP_ITEM_HEIGHT,
  };
}

/** 条目内「缩略图 / 文件信息」区：占满上半部分。*/
export function stripThumbLayout(item: BoxLayout): BoxLayout {
  return {
    x: item.x,
    y: item.y,
    width: item.width,
    height: item.height - STRIP_REMOVE_SIZE,
  };
}

/** 条目内「移除」按钮：底部居中，44 x 44。*/
export function stripRemoveLayout(item: BoxLayout): BoxLayout {
  return {
    x: item.x + (item.width - STRIP_REMOVE_SIZE) / 2,
    y: item.y + item.height - STRIP_REMOVE_SIZE,
    width: STRIP_REMOVE_SIZE,
    height: STRIP_REMOVE_SIZE,
  };
}

/** 消息内附件区的排布参数。*/
export const LIST_IMAGE_TILE = 72;
export const LIST_IMAGE_GAP = 8;
export const LIST_FILE_HEIGHT = 52;
export const LIST_ROW_GAP = 8;

/** 消息内第 index 张图片的位置。*/
export function listImageLayout(parent: BoxLayout, index: number): BoxLayout {
  return {
    x: parent.x + index * (LIST_IMAGE_TILE + LIST_IMAGE_GAP),
    y: parent.y,
    width: LIST_IMAGE_TILE,
    height: LIST_IMAGE_TILE,
  };
}

/** 消息内第 index 个文件卡片的位置（接在图片行之下）。*/
export function listFileLayout(parent: BoxLayout, index: number, imageCount: number): BoxLayout {
  const top = parent.y + (imageCount > 0 ? LIST_IMAGE_TILE + LIST_ROW_GAP : 0);
  return {
    x: parent.x,
    y: top + index * (LIST_FILE_HEIGHT + LIST_ROW_GAP),
    width: parent.width,
    height: LIST_FILE_HEIGHT,
  };
}